import { compact, debounce, take, upperFirst } from 'lodash/fp';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import { Country, FormValues, LocationResult, RenderItemProps, RenderMenuProps } from '../../types';
import { FIELD_NAMES } from '../../../../constants';
import { StyledFormikField } from '../step-input/styles';
import getCountriesBySearch from '../../utils/get-countries-by-search';
import Item from './components/auto-complete-item';
import Menu from './components/auto-complete-menu';
import NoResultsMessage from './components/no-results';
import PortalWrapper from '../portal-wrapper';
import useTravelWidgetContext from '../../hooks/use-travel-widget-context';

const MIN_QUERY_LENGTH = 2;
const QUERY_DELAY = 300;
const MAX_RESULTS = 5;

type AutoCompleteInputProps = {
  autoCompleteRef: React.RefObject<HTMLDivElement>;
  modalContentRef: React.RefObject<HTMLDivElement>;
  name: string;
  placeholder: string;
  setIsModalOpen: (value: boolean) => void;
  setIsSuggestionsOpen: (value: boolean) => void;
};

const AutoCompleteInput = ({
  autoCompleteRef,
  modalContentRef,
  name,
  placeholder,
  setIsSuggestionsOpen,
  setIsModalOpen,
}: AutoCompleteInputProps) => {
  const { countries, setCountries } = useTravelWidgetContext();
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [autoCompleteLoading, setAutoCompleteLoading] = useState(false);
  const [autoCompleteMenuItems, setAutoCompleteMenuItems] = useState<LocationResult[] | null>(null);

  useEffect(() => {
    const getCountries = async () => {
      const countriesList: Country[] = await fetch('/travel-insurance-widget/countries.json').then((response) =>
        response.json(),
      );
      setCountries(countriesList);
    };
    getCountries();
  }, []);

  const onSearchValueUpdated = () => (innerValue: string) => {
    if (innerValue.length < MIN_QUERY_LENGTH) {
      setAutoCompleteMenuItems(null);
      setIsSuggestionsOpen(false);
      return;
    }
    setIsSuggestionsOpen(true);
    setIsModalOpen(false);
    setAutoCompleteLoading(true);
    const result = compact(getCountriesBySearch(countries, innerValue));
    const limitedResult = take(MAX_RESULTS, result);
    setAutoCompleteLoading(false);
    setAutoCompleteMenuItems(limitedResult);
  };
  return (
    <StyledFormikField
      label={`${upperFirst(name)}`}
      name={name}
      required
      size="auto"
      placeholder={placeholder}
      htmlFor={name}
      id={name}
      onSearchValueUpdated={debounce(QUERY_DELAY, onSearchValueUpdated())}
      onSelectedValueChange={(value: string) => {
        setIsModalOpen(false);
        setIsSuggestionsOpen(false);
        setFieldValue(FIELD_NAMES.DESTINATIONS, value);
      }}
      selectPropertyOnSelection="value" // specifies which property of the selected item should be used to update the form field value
      displayPropertyOnSelection="label" // specifies which property of the selected item should be displayed in the input field when an item is selected
      type="autocompleteFormik"
      items={autoCompleteMenuItems}
      isLoading={autoCompleteLoading}
      renderNoResults={() => (
        <PortalWrapper hasRef={!!modalContentRef.current} modalContentRef={modalContentRef}>
          <NoResultsMessage />
        </PortalWrapper>
      )}
      renderItem={({ item, index, getItemProps, highlightedIndex }: RenderItemProps) => (
        <Item
          item={item}
          highlightedIndex={highlightedIndex}
          getItemProps={getItemProps}
          index={index}
          value={item.country}
          searchTerm={values.where}
        />
      )}
      renderMenu={(renderProps: RenderMenuProps) => (
        <PortalWrapper hasRef={!!modalContentRef.current} modalContentRef={modalContentRef}>
          <Menu {...renderProps} />
        </PortalWrapper>
      )}
      inputVariant="unstyled"
      innerRef={autoCompleteRef}
      hideErrorMessage
    />
  );
};

export default AutoCompleteInput;
